// src/UserControls/Payment/SubscriptionService.js
import configDetails from "../../Config/Config";
import { Auth } from 'aws-amplify';
import Logger from "../../utils/Logger";

const parseSmart = async (resp) => {
  // Clone the response so we can read it multiple times if needed
  const respClone = resp.clone();

  try {
    // Try JSON first
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      return await resp.json();
    }

    // If not JSON content-type, try to parse as text and then JSON
    const text = await resp.text();
    try {
      return JSON.parse(text);
    } catch {
      return { raw: text };
    }
  } catch (error) {
    // If the original response failed, try the cloned one as text
    try {
      const text = await respClone.text();
      try {
        return JSON.parse(text);
      } catch {
        return { raw: text };
      }
    } catch (secondError) {
      // If all fails, return the text error
      return { raw: error.message || "Failed to parse response" };
    }
  }
};

// Map snake_case (API) -> camelCase (UI)
const normalizePlan = (p) => {
  try {
    const normalized = {
      id: p.plan_id,
      name: p.plan_name,
      code: p.plan_code,
      mnemonic: p.plan_mnemonic,
      description: p.plan_description,
      type: p.plan_type,
      status: p.plan_status,
      price: Number(p.price),
      currency: p.currency_code,
      currencySymbol: p.currency_symbol,
      startDate: p.start_date,
      endDate: p.end_date,
      features: (() => { try { return JSON.parse(p.features ?? "[]"); } catch { return []; } })(),
      pricingId: p.pricing_id ?? p.pricingId ?? null,
    };
    Logger.debug("🔄 Normalized plan", normalized);
    return normalized;
  } catch (error) {
    Logger.error("❌ Failed to normalize plan", { error, rawPlan: p });
    throw error;
  }
};

const isPlanActiveAndCurrent = (plan) => {
  const status = (plan.status || "").trim().toUpperCase();
  if (status !== "ACTIVE") {
    Logger.debug("📅 Plan inactive", { status });
    return false;
  }
  const today = new Date().toISOString().slice(0, 10);
  const start = plan.startDate || "0000-01-01";
  const end   = plan.endDate   || "9999-12-31";
  const isValid = start <= today && today <= end;
  Logger.debug("📅 Plan date validation", { isValid, start, end, today });
  return isValid;
};

const getPlansByCountry = async (countryCode) => {
  let token;
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    token = currentUser.signInUserSession.idToken.jwtToken;
  } catch (error) {
    Logger.error("🚫 Missing auth token");
    throw new Error("Authorization token missing");
  }

  const url = `${configDetails.apiBaseUrlMicroservice}${configDetails.appActivePlans}/${countryCode}`;
  Logger.info(`🌍 Fetching plans: ${url}`);

  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!resp.ok) {
      const err = await parseSmart(resp);
      Logger.error("❌ Failed to fetch plans", { status: resp.status, error: err });
      const errorMsg = extractErrorMessage(err) || `HTTP ${resp.status} ${resp.statusText}`;
      throw new Error(errorMsg);
    }

    const raw = await parseSmart(resp);
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.plans) ? raw.plans : [];
    const normalized = list.map(normalizePlan);
    const filtered = normalized.filter(isPlanActiveAndCurrent);
    
    Logger.info("✅ Plans fetched successfully", {
      total: list.length,
      active: filtered.length
    });
    
    return filtered;
  } catch (error) {
    Logger.error("❌ Error fetching plans", { error });
    throw error;
  }
};

// ───────── Create order (InitiateOrderRequestDTO shape) ─────────
const normalizeCountry = (c) => {
  const cc = (c || "US").trim().toUpperCase();
  Logger.debug("🌍 Normalized country code", { input: c, output: cc });
  return cc.length === 2 ? cc : "US";
};

const createPaymentIntent = async (plan, billing, paymentType = 'STRIPE') => {
  let token, currentUser, userId;
  try {
    currentUser = await Auth.currentAuthenticatedUser();
    token = currentUser.signInUserSession.idToken.jwtToken;
    userId = currentUser.attributes.sub || currentUser.id || currentUser.userId;
  } catch (error) {
    Logger.error("🚫 Missing auth token");
    throw new Error("Authorization token missing");
  }

  // Payload structure matching backend expectations
  const payload = {
    cognitoUserId: userId,
    planId: plan.id,
    pricingId: plan.pricingId,
    quantity: 1,
    paymentHandlerType: paymentType,
    couponCode: billing?.couponCode ?? null,
    paymentRequestSource: window?.location?.origin,
    billingAddress: billing // { addressId: number }
  };

  const url = `${configDetails.apiBaseUrlOrderservice}${configDetails.createOrder}`;
  Logger.info(`💳 Creating order`, { url, planId: plan.id });
  Logger.debug("📦 InitiateOrderRequestDTO", payload);

  try {
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      const err = await parseSmart(resp);
      Logger.error("❌ Failed to create order", { status: resp.status, error: err });
      const errorMsg = extractErrorMessage(err) || `HTTP ${resp.status} ${resp.statusText}`;
      throw new Error(errorMsg);
    }

    const data = await parseSmart(resp);
    
    // Handle different response formats for different payment types
    if (paymentType === 'PAYPAL') {
      // PayPal response format: { payment: { paypal: { paypalOrderId, approvalLink } } }
      const paypalData = data.payment?.paypal;
      const orderId = paypalData?.paypalOrderId;
      const approvalLink = paypalData?.approvalLink;
      
      if (!orderId || !approvalLink) {
        throw new Error("Missing PayPal orderId or approval link");
      }

    const orderNumber = data.order?.orderNumber;

      // Return structure with orderNumber and paypalOrderId as paymentId
      return { 
        paymentType: 'PAYPAL', 
        orderId,
        orderNumber,
        paymentId: orderId,
        approvalLink,
        payment: { orderId, approvalLink, orderNumber, paymentId: orderId },
        order: data.order
      };
    } else {
      // Stripe response format: { payment: { stripe: { clientSecret } } }
      const stripeData = data.payment?.stripe;
      const clientSecret =
        stripeData?.clientSecret ||
        stripeData?.client_secret ||
        data.payment?.clientSecret ||
        data.payment?.client_secret ||
        null;
      
      if (!clientSecret) {
        Logger.error("❌ No client secret in order response", { data });
        throw new Error("Missing client secret in order response");
      }

      return { paymentType: 'STRIPE', clientSecret, order: data };
    }
  } catch (error) {
    Logger.error("❌ Error creating order", { error });
    throw error;
  }
};

// Recursively extract first string value from error object
function extractErrorMessage(err) {
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    for (const key of ['message', 'error', 'detail', 'warning', 'log', 'raw']) {
      if (err[key]) {
        const val = extractErrorMessage(err[key]);
        if (val) return val;
      }
    }
    // Fallback: search all values
    for (const val of Object.values(err)) {
      const msg = extractErrorMessage(val);
      if (msg) return msg;
    }
  }
  return null;
}
const PAYPAL_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  APPROVED: 'APPROVED',
  COMPLETED: 'COMPLETED',
  CAPTURE_PENDING: 'CAPTURE_PENDING',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REQUIRES_ACTION: 'REQUIRES_ACTION',
  DISPUTED: 'DISPUTED',
};
const PAYMENT_STATE = {
  IN_PROGRESS: '0',
  COMPLETED: '1',
  FAILED: '2',
  REFUNDED: '3',
  ACTION_NEEDED: '4',
  DISPUTED: '5',
};

const isPollingStatus = (status, paymentState = null) => {
  if (paymentState !== null) {
    return paymentState === PAYMENT_STATE.IN_PROGRESS;
  }
  return [
    PAYPAL_STATUS.PENDING,
    PAYPAL_STATUS.PROCESSING,
    PAYPAL_STATUS.APPROVED,
    PAYPAL_STATUS.CAPTURE_PENDING,
  ].includes(status);
};

const isSuccessStatus = (status, paymentState = null) => {
  if (paymentState !== null) {
    return paymentState === PAYMENT_STATE.COMPLETED;
  }
  return [
    PAYPAL_STATUS.COMPLETED,
    PAYPAL_STATUS.CAPTURE_PENDING,
  ].includes(status);
};

const isFailureStatus = (status, paymentState = null) => {
  if (paymentState !== null) {
    return paymentState === PAYMENT_STATE.FAILED;
  }
  return [
    PAYPAL_STATUS.FAILED,
    PAYPAL_STATUS.CANCELLED,
  ].includes(status);
};

const isRefundedStatus = (paymentState) => {
  return paymentState === PAYMENT_STATE.REFUNDED;
};

const isActionNeededStatus = (paymentState) => {
  return paymentState === PAYMENT_STATE.ACTION_NEEDED;
};

const isDisputedStatus = (paymentState) => {
  return paymentState === PAYMENT_STATE.DISPUTED;
};

const getPaymentFailureMessage = (status) => {
  switch (status) {
    case PAYPAL_STATUS.FAILED:
      return "Payment failed. Please try again or use a different payment method.";
    case PAYPAL_STATUS.CANCELLED:
      return "Payment was cancelled.";
    case PAYPAL_STATUS.REQUIRES_ACTION:
      return "Additional action required. Please check your PayPal account.";
    case PAYPAL_STATUS.DISPUTED:
      return "Payment is under dispute. Please contact support.";
    case PAYPAL_STATUS.REFUNDED:
      return "Payment has been refunded.";
    case PAYPAL_STATUS.PARTIALLY_REFUNDED:
      return "Payment has been partially refunded.";
    default:
      return "Payment could not be completed. Please try again.";
  }
};

/**
 * Get PayPal payment status from backend
 * @param {string} paypalOrderId - The PayPal Order ID
 * @returns {Promise<object>} Payment status response
 */
const getPayPalPaymentStatus = async (paypalOrderId) => {
  let token;
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    token = currentUser.signInUserSession.idToken.jwtToken;
  } catch (error) {
    Logger.error("🚫 Missing auth token for payment status check");
    throw new Error("Authorization token missing");
  }

  const url = `${configDetails.apiBaseUrlOrderservice}${configDetails.getPaymentStatusEndpoint}/${paypalOrderId}`;
  Logger.debug("🔄 Fetching payment status", { url, paypalOrderId });

  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!resp.ok) {
      const err = await parseSmart(resp);
      Logger.error("❌ Failed to fetch payment status", { status: resp.status, error: err });
      const errorMsg = extractErrorMessage(err) || `HTTP ${resp.status} ${resp.statusText}`;
      throw new Error(errorMsg);
    }

    const data = await parseSmart(resp);
  
    // paymentState = UI state code (0=In Progress, 1=Completed, 2=Failed, 3=Refunded, 4=Action Needed, 5=Disputed)
    const status = data.paymentStatus || data.payment_status || data.status;
    const paymentState = data.pay
    return {
      success: true,
      orderId: data.orderId,
      paypalOrderId: data.paypalOrderId,
      status: status,
      paymentState: paymentState,
      message: data.message || null,
      updatedAt: data.updatedAt,
      orderNumber: data.orderNumber,
      amount: data.amount,
    };
  } catch (error) {
    Logger.error("❌ Error fetching payment status", { error, paypalOrderId });
    throw error;
  }
};

/**
 * Poll payment status until completion, failure, or timeout
 * @param {string} paypalOrderId - The PayPal Order ID
 * @param {object} options - Polling options
 * @param {function} onStatusChange - Callback for status updates
 * @returns {Promise<object>} Final payment status
 */
const pollPaymentStatus = async (paypalOrderId, options = {}, onStatusChange = null) => {
  const {
    interval = configDetails.paypalPolling?.interval || 2000,
    timeout = configDetails.paypalPolling?.timeout || 60000,
    maxAttempts = configDetails.paypalPolling?.maxAttempts || 30,
  } = options;

  const startTime = Date.now();
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      attempts++;
      const elapsedTime = Date.now() - startTime;

      // Check timeout
      if (elapsedTime >= timeout) {
        resolve({
          success: false,
          status: 'TIMEOUT',
          message: "Your payment is being processed. This may take a few more moments. You'll receive a confirmation email once complete.",
          isTimeout: true,
          isProcessing: true,
        });
        return;
      }

      // Check max attempts
      if (attempts > maxAttempts) {
        Logger.warn("🔄 Payment status polling max attempts reached", { 
          paypalOrderId, 
          attempts 
        });
        resolve({
          success: false,
          status: 'MAX_ATTEMPTS',
          message: "Your payment is being processed. This may take a few more moments. You'll receive a confirmation email once complete.",
          isTimeout: true,
          isProcessing: true,
        });
        return;
      }

      try {
        const statusResponse = await getPayPalPaymentStatus(paypalOrderId);
        const { status, paymentState } = statusResponse;

        // Notify callback of status change
        if (onStatusChange) {
          onStatusChange(statusResponse, attempts, elapsedTime);
        }

        // Check if success (paymentState "1" = Completed)
        if (isSuccessStatus(status, paymentState)) {
          resolve({
            ...statusResponse,
            success: true,
            isCompleted: true,
          });
          return;
        }

        // Check if failure (paymentState "2" = Failed)
        if (isFailureStatus(status, paymentState)) {
          resolve({
            ...statusResponse,
            success: false,
            isFailed: true,
            message: statusResponse.message || getPaymentFailureMessage(status),
          });
          return;
        }

        // Check if refunded (paymentState "3" = Refunded)
        if (isRefundedStatus(paymentState)) {
          resolve({
            ...statusResponse,
            success: false,
            isRefunded: true,
          });
          return;
        }

        // Check if action needed (paymentState "4" = Action Needed)
        if (isActionNeededStatus(paymentState)) {
          resolve({
            ...statusResponse,
            success: false,
            isActionNeeded: true,
          });
          return;
        }

        // Check if disputed (paymentState "5" = Disputed)
        if (isDisputedStatus(paymentState)) {
          resolve({
            ...statusResponse,
            success: false,
            isDisputed: true,
          });
          return;
        }

        // Still in progress (paymentState "0" = In Progress), continue polling
        if (isPollingStatus(status, paymentState)) {
          setTimeout(poll, interval);
          return;
        }
        setTimeout(poll, interval);

      } catch (error) {
        // Continue polling on error (might be temporary network issue)
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          resolve({
            success: false,
            status: 'ERROR',
            message: "Failed to verify payment status. Please refresh or contact support.",
            error: error.message,
          });
        }
      }
    };
    poll();
  });
};

// Legacy function for backward compatibility
const capturePayPalPayment = async (orderId, payerId, status = PAYPAL_STATUS.COMPLETED) => {
  const isSuccess = isSuccessStatus(status);
  
  return {
    success: isSuccess,
    orderId: orderId,
    payerId: payerId,
    status: status,
    message: isSuccess 
      ? "PayPal will notify backend via webhook" 
      : getPaymentFailureMessage(status)
  };
};

const SubscriptionServiceExport = {
  getPlansByCountry,
  createPaymentIntent,
  capturePayPalPayment,
  getPayPalPaymentStatus,
  pollPaymentStatus,
  PAYPAL_STATUS,
  PAYMENT_STATE,
  isPollingStatus,
  isSuccessStatus,
  isFailureStatus,
  isRefundedStatus,
  isActionNeededStatus,
  isDisputedStatus,
  getPaymentFailureMessage
};

export default SubscriptionServiceExport;
